import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { CommonService } from './service/common.service';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UserService } from './service/user.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private userService: UserService,
    public _commonService: CommonService,
    public cookieService: CookieService,
    private toastr: ToastrService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this._commonService.show();
    const authToken = this.cookieService.get('authToken');

    // Get language from localStorage
    const selectedLanguage = localStorage.getItem('selectedLanguage') || 'de';

    // Check if request already has Authorization header (e.g., PDF downloads)
    const hasAuthHeader = req.headers.has('Authorization');

    if (authToken && !this.isTokenExpired(authToken) && !hasAuthHeader) {
      // Add both Authorization and Accept-Language headers
      let headers = req.headers
        .set('Authorization', 'Bearer ' + authToken)
        .set('Accept-Language', selectedLanguage);
      const authReq = req.clone({ headers });
      if (req.url.includes('questionUpload')) {
        const modifiedReq = req.clone({ headers: req.headers.set('Authorization', 'Bearer ') });
        return next.handle(modifiedReq);
      }
      return next.handle(authReq).pipe(
        catchError(error => {
          // Zustimmung zu den Rechtsdokumenten fehlt oder ist veraltet: auf die Zustimmungsseite
          if (error.status === 403 && error.error?.code === 'consent_required') {
            if (!this.router.url.startsWith('/consent')) {
              this.router.navigate(['/consent']);
            }
            return throwError(error?.error?.detail || error?.detail);
          }
          // Handle authentication-related errors (not permission errors)
          if (error.error?.detail === 'Token has expired' || error?.detail === 'Authentication credentials were not provided.' || error.status === 401) {
            this.clearSession(); // Clear session on token expiration or authentication error
            this.router.navigate(['/login']);
          }
          // Don't redirect to login on 403 (permission denied) - let the component handle it
          // Die Löschanfragen der Admins brauchen den Fehlercode aus dem Text der Antwort
          // Die Report-Seite zeigt Fehler nach Code (z. B. unzulässige Vergleichskombination), nicht nach dem Text
          if (req.url.includes('/report/profile/') && error.error && typeof error.error === 'object') {
            return throwError(error.error);
          }
          if (req.url.includes('/data-requests/') && error.error && typeof error.error === 'object') {
            return throwError(error.error);
          }
          return throwError(error?.error?.detail || error?.detail);
        }),
        finalize(() => {
          this._commonService.hide();
        })
      );
    } else if (hasAuthHeader) {
      // Request already has auth header, add language and forward with error handling
      console.log('[Interceptor] Request already has Authorization header, forwarding as-is:', req.headers.get('Authorization')?.substring(0, 20) + '...');
      const langReq = req.clone({ headers: req.headers.set('Accept-Language', selectedLanguage) });
      return next.handle(langReq).pipe(
        catchError(error => {
          console.error('[HTTP] Error in request with custom auth:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this._commonService.hide();
        })
      );
    } else {
      this.clearSession();
      this._commonService.hide();
      // this.router.navigate(['/login']);
      // Even for unauthenticated requests, add language header
      const langReq = req.clone({ headers: req.headers.set('Accept-Language', selectedLanguage) });
      return next.handle(langReq);
    }
  }

  // Helper method to determine if the token is expired
  private isTokenExpired(token: string): boolean {
    const expiryDate = this.getTokenExpiryDate(token);
    if (!expiryDate) return false;
    return expiryDate.getTime() < new Date().getTime();
  }

  // Helper to extract the expiration date from the JWT
  private getTokenExpiryDate(token: string): Date | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // Decode the token payload
      if (payload.exp) {
        return new Date(payload.exp * 1000); // Convert expiry time to JavaScript Date object
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  private clearSession() {
    localStorage.clear();
    this.cookieService.deleteAll();
  }

}
